/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from "rxjs";

// PrimeNG imports
import { ButtonModule } from "primeng/button";
import { DatePickerModule } from "primeng/datepicker";
import { CheckboxModule } from "primeng/checkbox";
import { ChipsModule } from "primeng/chips";
import { DividerModule } from "primeng/divider";
import { FieldsetModule } from "primeng/fieldset";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import { MultiSelectModule } from "primeng/multiselect";

// Our interfaces
import { ProcessingErrorCodesHelper } from "../../../settings/utils/processing-error-codes.helper";
import { ScheduledOrderCounts } from "../../interfaces/scheduled-orders-counts.interface";
import {
  DateRange,
  DEFAULT_FILTER_VALUES,
  IScheduledOrdersFilterControls,
  ProcessingErrorCodeOption,
  ScheduledOrdersFilter,
  StatusFilterOption,
} from "../../interfaces/scheduled-orders-filter.interface";

/**
 * Scheduled Orders Filters Component
 * Handles all filtering logic with strongly typed reactive forms
 * Follows Input/Output pattern - receives form from parent, emits changes
 */
@Component({
  selector: "app-scheduled-orders-filters",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    CheckboxModule,
    MultiSelectModule,
    DatePickerModule,
    InputTextModule,
    ButtonModule,
    DividerModule,
    IconFieldModule,
    InputIconModule,
    ChipsModule,
    FieldsetModule,
  ],
  templateUrl: "./scheduled-orders-filters.component.html",
  styleUrl: "./scheduled-orders-filters.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduledOrdersFiltersComponent
  implements OnInit, OnDestroy, OnChanges
{
  // Input/Output pattern
  @Input() filterForm!: FormGroup<IScheduledOrdersFilterControls>;
  @Input() counts: ScheduledOrderCounts | null = null;
  @Input() loading = false;
  @Input() sidebarMode = false; // Legacy sidebar layout
  @Input() drawerMode = false; // New drawer layout mode
  @Input() showCreateButton = true; // Control visibility of Create New button (hidden for Shopify sites)
  @Input() skipLocalStorageLoad = false; // Skip loading from localStorage (used when parent provides filters via query params)

  @Output() filtersChanged = new EventEmitter<ScheduledOrdersFilter>();
  @Output() clearFilters = new EventEmitter<void>();
  @Output() createNew = new EventEmitter<void>();

  // Component state
  private readonly destroy$ = new Subject<void>();

  // UI state
  isDateFiltersCollapsed = true;
  showAdvancedDates = false;

  // Date constraints
  maxDate = new Date(); // Today's date - past-only constraint for Last Processed, Order Created, Customer Created

  // Filter options
  statusOptions: StatusFilterOption[] = [];
  processingErrorCodeOptions: ProcessingErrorCodeOption[] = [];

  // Status filter configuration for tab-style buttons
  statusTabOptions = [
    { value: "All", label: "All" },
    { value: "Active", label: "Active" },
    { value: "Paused", label: "Paused" },
    { value: "Failed", label: "Failed" },
    { value: "Queued", label: "Queued" },
    { value: "Processing", label: "Processing" },
    { value: "Locked", label: "Locked" },
    { value: "Deleted", label: "Deleted" },
  ];

  // Computed properties
  get showProcessingErrorCodes(): boolean {
    const statusValue = this.filterForm?.controls.status.value || [];
    return statusValue.includes("Failed");
  }

  get hasActiveFilters(): boolean {
    if (!this.filterForm) return false;

    const formValue = this.filterForm.value;

    // Return true if ANY filter is selected (including status)
    // This ensures the Clear All button is enabled when filters are visible
    return !!(
      formValue.search?.trim() ||
      (formValue.status && formValue.status.length > 0) ||
      (formValue.processingErrorCodes &&
        formValue.processingErrorCodes.length > 0) ||
      (Array.isArray(formValue.customerCreatedRange) &&
        formValue.customerCreatedRange.length > 0) ||
      (Array.isArray(formValue.scheduledOrderCreatedRange) &&
        formValue.scheduledOrderCreatedRange.length > 0) ||
      (Array.isArray(formValue.nextOccurrenceRange) &&
        formValue.nextOccurrenceRange.length > 0) ||
      (Array.isArray(formValue.lastProcessedRange) &&
        formValue.lastProcessedRange.length > 0)
    );
  }

  /**
   * Get selected status summary with counts
   * Returns array of objects with status name and count for display
   */
  get selectedStatusSummary(): { status: string; count: number }[] {
    const selectedStatuses = this.filterForm?.controls.status.value || [];
    if (!selectedStatuses.length || !this.counts) return [];

    return selectedStatuses.map((status: string) => ({
      status,
      count: this.counts![status as keyof ScheduledOrderCounts] || 0,
    }));
  }

  constructor(private processingErrorCodesHelper: ProcessingErrorCodesHelper) {}

  ngOnInit(): void {
    this.initializeFilterOptions();
    // Only load from localStorage if skipLocalStorageLoad is false
    // When navigating from customers page with search query param, parent sets the form values
    // and we should NOT override them with localStorage values
    if (!this.skipLocalStorageLoad) {
      this.loadPersistedFilters(); // Load filters BEFORE setting up subscriptions
    }
    this.setupFormSubscriptions();

    // Show advanced dates if they're already active
    this.showAdvancedDates = this.hasAdvancedDateFilters();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["counts"] && this.counts) {
      this.updateStatusOptionsWithCounts();
    }
  }

  /**
   * Initialize filter options (status and error codes)
   */
  private initializeFilterOptions(): void {
    // Status options
    this.statusOptions = [
      { value: "Active" as any, label: "Active" },
      { value: "Paused" as any, label: "Paused" },
      { value: "Failed" as any, label: "Failed" },
      { value: "Queued" as any, label: "Queued" },
      { value: "Processing" as any, label: "Processing" },
      { value: "Locked" as any, label: "Locked" },
      { value: "Deleted" as any, label: "Deleted" },
    ];

    // Processing error code options - show ALL available codes with numbers in parentheses
    const processingErrorCodeOptionsMap =
      this.processingErrorCodesHelper.getProcessingErrorCodeOptions();
    this.processingErrorCodeOptions = Object.entries(
      processingErrorCodeOptionsMap,
    ).map(([code, description]) => ({
      code: parseInt(code),
      description: description,
    }));

    this.updateStatusOptionsWithCounts();
  }

  /**
   * Update status options with current counts
   */
  private updateStatusOptionsWithCounts(): void {
    if (!this.counts) return;

    this.statusOptions = this.statusOptions.map((option) => ({
      ...option,
      label: `${option.value} (${this.counts![option.value as keyof ScheduledOrderCounts] || 0})`,
    }));
  }

  /**
   * Setup form subscriptions for real-time filtering
   */
  private setupFormSubscriptions(): void {
    if (!this.filterForm) return;

    // Debounce search input
    this.filterForm.controls.search.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.emitFilterChange();
      });

    // Debounced updates for other controls
    const debouncedControls = [
      "status",
      "processingErrorCodes",
      "customerCreatedRange",
      "scheduledOrderCreatedRange",
      "nextOccurrenceRange",
      "lastProcessedRange",
    ] as const;

    debouncedControls.forEach((controlName) => {
      const control = this.filterForm.controls[controlName] as any;
      control.valueChanges
        .pipe(debounceTime(500), takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            // For date range controls, only emit if both dates are selected or range is null/empty
            const isDateRangeControl = [
              "customerCreatedRange",
              "scheduledOrderCreatedRange",
              "nextOccurrenceRange",
              "lastProcessedRange",
            ].includes(controlName);

            if (isDateRangeControl) {
              const rangeValue = control.value;
              console.log(
                `📅 Date range control ${controlName} changed:`,
                rangeValue,
              );
              // Only emit if: null/empty OR both dates are present
              if (rangeValue === null || rangeValue === undefined) {
                // Range cleared - emit the change
                console.log("✅ Emitting because range is cleared");
                this.emitFilterChange();
                this.persistFilters();
              } else if (
                Array.isArray(rangeValue) &&
                rangeValue.length === 2 &&
                rangeValue[0] &&
                rangeValue[1]
              ) {
                // Both dates selected - emit the change
                console.log("✅ Emitting because both dates selected");
                this.emitFilterChange();
                this.persistFilters();
              } else {
                console.log(
                  `⛔ NOT emitting because incomplete range (length: ${Array.isArray(rangeValue) ? rangeValue.length : "not array"})`,
                );
              }
              // Otherwise, incomplete range - don't emit (search won't execute)
            } else {
              // Non-date controls - emit normally
              console.log(
                `🔄 Non-date control ${controlName} changed - emitting`,
              );
              this.emitFilterChange();
              this.persistFilters();
            }
          },
        });
    });

    // Handle status change for processing error codes visibility
    this.filterForm.controls.status.valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe({
        next: (statusValues: string[]) => {
          if (statusValues.includes("Failed")) {
            // Auto-select only the "needed" error codes (common ones) when Failed is selected
            const neededErrorCodes =
              this.processingErrorCodesHelper.getAvailableErrorCodes();
            this.filterForm.controls.processingErrorCodes.setValue(
              neededErrorCodes,
              { emitEvent: false },
            );
          } else {
            // Clear processing error codes if Failed is not selected
            this.filterForm.controls.processingErrorCodes.setValue([], {
              emitEvent: false,
            });
          }
        },
      });
  }

  /**
   * Emit filter changes to parent component
   */
  private emitFilterChange(): void {
    if (!this.filterForm) return;

    const formValue = this.filterForm.value;
    const filter: ScheduledOrdersFilter = {
      siteId: formValue.siteId!,
      search: formValue.search?.trim() || undefined,
      // Always send status - default to Active if empty (per David's spec)
      status:
        formValue.status && formValue.status.length > 0
          ? formValue.status
          : ["Active"],
      processingErrorCodes:
        formValue.processingErrorCodes &&
        formValue.processingErrorCodes.length > 0
          ? formValue.processingErrorCodes
          : undefined,
      pageSize: formValue.pageSize!,
      page: formValue.page!,
      orderBy: formValue.sortBy || undefined,
      order: formValue.sortDirection === "desc" ? "desc" : "asc",

      // Date ranges
      ...this.extractDateRangeFilters(formValue),
    };

    this.filtersChanged.emit(filter);
  }

  /**
   * Extract date range filters from form values
   * PrimeNG datepicker in range mode returns Date arrays [Date, Date], not DateRange objects
   */
  private extractDateRangeFilters(
    formValue: any,
  ): Partial<ScheduledOrdersFilter> {
    const dateFilters: Partial<ScheduledOrdersFilter> = {};

    // Helper to normalize date range (handles Date array from PrimeNG)
    // Also normalizes times: start dates to 00:00:00, end dates to 23:59:59
    const normalizeDateRange = (
      range: Date[] | null,
    ): { start: Date; end: Date } | null => {
      if (!range) return null;

      // Handle Date array format from PrimeNG datepicker (range mode)
      // Require BOTH dates in the array for range filter
      if (Array.isArray(range) && range.length > 0 && range[0]) {
        // If only start date is selected, return null to prevent incomplete filter
        if (!range[1]) return null;

        // Normalize times to match old QMC behavior:
        // Start date: 00:00:00.000 (beginning of day)
        // End date: 23:59:59.999 (end of day)
        const normalizedStart = new Date(range[0]);
        normalizedStart.setHours(0, 0, 0, 0);

        const normalizedEnd = new Date(range[1]);
        normalizedEnd.setHours(23, 59, 59, 999);

        return {
          start: normalizedStart,
          end: normalizedEnd,
        };
      }

      return null;
    };

    // Customer created range
    const customerRange = normalizeDateRange(
      formValue.customerCreatedRange as Date[] | null,
    );
    if (customerRange) {
      dateFilters.customerCreatedFromDate = customerRange.start;
      dateFilters.customerCreatedToDate = customerRange.end;
    }

    // Scheduled order created range
    const scheduledOrderRange = normalizeDateRange(
      formValue.scheduledOrderCreatedRange as Date[] | null,
    );
    if (scheduledOrderRange) {
      dateFilters.scheduledOrderCreatedFromDate = scheduledOrderRange.start;
      dateFilters.scheduledOrderCreatedToDate = scheduledOrderRange.end;
    }

    // Next occurrence range (startDate/endDate in API)
    const nextOccurrenceRange = normalizeDateRange(
      formValue.nextOccurrenceRange as Date[] | null,
    );
    if (nextOccurrenceRange) {
      dateFilters.startDate = nextOccurrenceRange.start;
      dateFilters.endDate = nextOccurrenceRange.end;
    }

    // Last processed range
    const lastProcessedRange = normalizeDateRange(
      formValue.lastProcessedRange as Date[] | null,
    );
    if (lastProcessedRange) {
      dateFilters.lastProcessedFromDate = lastProcessedRange.start;
      dateFilters.lastProcessedToDate = lastProcessedRange.end;
    }

    return dateFilters;
  }

  /**
   * Clear all filters
   */
  onClearFilters(): void {
    if (!this.filterForm) return;

    // Reset form to default values
    this.filterForm.patchValue({
      search: null,
      status: [],
      processingErrorCodes: [],
      customerCreatedRange: null,
      scheduledOrderCreatedRange: null,
      nextOccurrenceRange: null,
      lastProcessedRange: null,
      page: DEFAULT_FILTER_VALUES.page,
      pageSize: DEFAULT_FILTER_VALUES.pageSize,
      sortBy: DEFAULT_FILTER_VALUES.sortBy,
      sortDirection: DEFAULT_FILTER_VALUES.sortDirection,
    });

    this.clearPersistedFilters();
    this.clearFilters.emit();
  }

  /**
   * Clear all filters with ultra mode
   */
  onClearAllUltra(): void {
    if (!this.filterForm) return;

    // Reset form to default values (same as regular clear for now)
    this.filterForm.patchValue({
      search: null,
      status: [],
      processingErrorCodes: [],
      customerCreatedRange: null,
      scheduledOrderCreatedRange: null,
      nextOccurrenceRange: null,
      lastProcessedRange: null,
      page: DEFAULT_FILTER_VALUES.page,
      pageSize: DEFAULT_FILTER_VALUES.pageSize,
      sortBy: DEFAULT_FILTER_VALUES.sortBy,
      sortDirection: DEFAULT_FILTER_VALUES.sortDirection,
    });

    // Hide advanced dates section
    this.showAdvancedDates = false;

    this.clearPersistedFilters();
    this.clearFilters.emit();
  }

  /**
   * Handle status checkbox changes
   */
  onStatusChange(status: string, checked: boolean): void {
    if (!this.filterForm) return;

    const currentStatus = this.filterForm.controls.status.value || [];
    let newStatus: string[];

    if (checked) {
      newStatus = [...currentStatus, status];
    } else {
      newStatus = currentStatus.filter((s) => s !== status);
    }

    this.filterForm.controls.status.setValue(newStatus);
  }

  /**
   * Handle date range selection
   */
  onDateRangeChange(
    controlName: keyof IScheduledOrdersFilterControls,
    dateRange: Date[],
  ): void {
    if (!this.filterForm) return;

    const range: DateRange | null =
      dateRange && dateRange.length > 0
        ? {
            startDate: dateRange[0] || null,
            endDate: dateRange[1] || dateRange[0] || null,
          }
        : null;

    (this.filterForm.controls[controlName] as any).setValue(range);
  }

  /**
   * Get date range for PrimeNG Calendar
   * PrimeNG already stores Date arrays, so just return the value
   */
  getDateRangeValue(
    controlName: keyof IScheduledOrdersFilterControls,
  ): Date[] | null {
    const value = (this.filterForm?.controls[controlName] as any)?.value;

    // If it's already a Date array, return it
    if (Array.isArray(value)) {
      return value.length > 0 ? value : null;
    }

    // If it's a DateRange object (shouldn't happen but handle it), convert to array
    if (value && typeof value === "object" && "startDate" in value) {
      const range = value as DateRange;
      if (!range.startDate) return null;
      return range.endDate
        ? [range.startDate, range.endDate]
        : [range.startDate];
    }

    return null;
  }

  /**
   * Persist filters to localStorage
   * Saves the current filter state as-is (empty status = show all)
   * Default ["Active"] is only applied on initial page load, not when saving
   */
  private persistFilters(): void {
    if (!this.filterForm) return;

    // Save current status as-is (don't default to ["Active"])
    // The default ["Active"] is only applied on initial page load
    const currentStatus = this.filterForm.controls.status.value || [];

    const filtersToSave = {
      search: this.filterForm.controls.search.value,
      status: currentStatus,
      processingErrorCodes: this.filterForm.controls.processingErrorCodes.value,
      customerCreatedRange: this.serializeDateRange(
        this.filterForm.controls.customerCreatedRange.value,
      ),
      scheduledOrderCreatedRange: this.serializeDateRange(
        this.filterForm.controls.scheduledOrderCreatedRange.value,
      ),
      nextOccurrenceRange: this.serializeDateRange(
        this.filterForm.controls.nextOccurrenceRange.value,
      ),
      lastProcessedRange: this.serializeDateRange(
        this.filterForm.controls.lastProcessedRange.value,
      ),
    };

    localStorage.setItem(
      "scheduledOrders.filters",
      JSON.stringify(filtersToSave),
    );
  }

  /**
   * Load persisted filters from localStorage
   * NOTE: Status always defaults to ["Active"] if empty - this is by design
   */
  private loadPersistedFilters(): void {
    if (!this.filterForm) return;

    try {
      const saved = localStorage.getItem("scheduledOrders.filters");
      if (saved) {
        const filters = JSON.parse(saved);

        // Get status from saved filters, default to ["Active"] if empty
        const savedStatus = filters.status;
        const statusToLoad =
          savedStatus && savedStatus.length > 0 ? savedStatus : ["Active"];

        // Only restore filter-specific values, not pagination/sorting
        // Use emitEvent: false to prevent subscription from firing before setup is complete
        this.filterForm.patchValue(
          {
            search: filters.search,
            status: statusToLoad,
            processingErrorCodes: filters.processingErrorCodes || [],
            customerCreatedRange: this.deserializeDateRange(
              filters.customerCreatedRange,
            ),
            scheduledOrderCreatedRange: this.deserializeDateRange(
              filters.scheduledOrderCreatedRange,
            ),
            nextOccurrenceRange: this.deserializeDateRange(
              filters.nextOccurrenceRange,
            ),
            lastProcessedRange: this.deserializeDateRange(
              filters.lastProcessedRange,
            ),
          },
          { emitEvent: false },
        );
      }
    } catch (error) {
      console.warn("Failed to load persisted filters:", error);
      this.clearPersistedFilters();
    }
  }

  /**
   * Clear persisted filters from localStorage
   */
  private clearPersistedFilters(): void {
    localStorage.removeItem("scheduledOrders.filters");
  }

  /**
   * Toggle date filters section
   */
  toggleDateFilters(): void {
    this.isDateFiltersCollapsed = !this.isDateFiltersCollapsed;
  }

  /**
   * Handle multi-select status selection
   */
  onStatusTabChange(statusValue: string): void {
    if (!this.filterForm) return;

    if (statusValue === "All") {
      // Clear all status filters for "All"
      this.filterForm.controls.status.setValue([]);
    } else {
      // Toggle status in multi-select mode
      const currentStatus = this.filterForm.controls.status.value || [];
      let newStatus: string[];

      if (currentStatus.includes(statusValue)) {
        // Remove if already selected
        newStatus = currentStatus.filter((s) => s !== statusValue);
      } else {
        // Add if not selected
        newStatus = [...currentStatus, statusValue];
      }

      this.filterForm.controls.status.setValue(newStatus);
    }
  }

  /**
   * Check if status is selected
   */
  isStatusSelected(statusValue: string): boolean {
    if (statusValue === "All") {
      const currentStatus = this.filterForm?.controls.status.value || [];
      return currentStatus.length === 0;
    }

    const currentStatus = this.filterForm?.controls.status.value || [];
    return currentStatus.includes(statusValue);
  }

  /**
   * Get status tab label with count
   */
  getStatusTabLabel(statusValue: string): string {
    if (!this.counts) return statusValue;

    if (statusValue === "All") {
      return `All (${this.counts.Total || 0})`;
    }

    const count = this.counts[statusValue as keyof ScheduledOrderCounts] || 0;
    return `${statusValue} (${count})`;
  }

  /**
   * Handle create new button click
   */
  onCreateNew(): void {
    this.createNew.emit();
  }

  /**
   * Remove a status from the selected statuses
   */
  removeStatus(statusToRemove: string): void {
    if (!this.filterForm) return;

    const currentStatus = this.filterForm.controls.status.value || [];
    const newStatus = currentStatus.filter(
      (status: string) => status !== statusToRemove,
    );
    this.filterForm.controls.status.setValue(newStatus);
  }

  /**
   * Get CSS class for search section based on mode
   */
  getSearchSectionClass(): string {
    if (this.drawerMode) {
      return "p-3";
    }
    return "mb-4";
  }

  /**
   * Toggle advanced date filters visibility
   */
  toggleAdvancedDates(): void {
    this.showAdvancedDates = !this.showAdvancedDates;
  }

  /**
   * Check if any advanced date filters are active
   */
  hasAdvancedDateFilters(): boolean {
    const scheduledOrderRange =
      this.filterForm?.controls.scheduledOrderCreatedRange?.value;
    const customerRange = this.filterForm?.controls.customerCreatedRange?.value;
    const lastProcessedRange =
      this.filterForm?.controls.lastProcessedRange?.value;

    return !!(
      (Array.isArray(scheduledOrderRange) && scheduledOrderRange.length > 0) ||
      (Array.isArray(customerRange) && customerRange.length > 0) ||
      (Array.isArray(lastProcessedRange) && lastProcessedRange.length > 0)
    );
  }

  /**
   * Serialize date range for localStorage (convert Date objects to ISO strings)
   * Handles both DateRange objects and Date arrays from PrimeNG
   */
  private serializeDateRange(range: DateRange | Date[] | null): any {
    if (!range) return null;

    // Handle Date array format from PrimeNG datepicker
    if (Array.isArray(range)) {
      if (range.length === 0 || !range[0]) return null;
      return {
        startDate: range[0] ? range[0].toISOString() : null,
        endDate: range[1] ? range[1].toISOString() : null,
      };
    }

    // Handle DateRange object format
    if (typeof range === "object" && "startDate" in range) {
      return {
        startDate: range.startDate ? range.startDate.toISOString() : null,
        endDate: range.endDate ? range.endDate.toISOString() : null,
      };
    }

    return null;
  }

  /**
   * Deserialize date range from localStorage (convert ISO strings back to Date array for PrimeNG)
   */
  private deserializeDateRange(range: any): Date[] | null {
    if (!range || !range.startDate) return null;

    // Return Date array format for PrimeNG datepicker
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const dates: Date[] = [new Date(range.startDate)];
    if (range.endDate) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      dates.push(new Date(range.endDate));
    }
    return dates;
  }
}
